/**
 * RCC Surface #7 — Escheatment / Dormancy Manager.
 *
 * Reads MO RSMo 447.539.9 rules from config.systemSettings + config.sdbTimeline
 * so the rules are authored server-side. Read-only UI — generation of NAUPA
 * XML reports is Surface #14 + RAS.
 */

import { useMemo } from 'react';
import { useConfig } from '@/contexts/ConfigContext';
import { Pill } from '@/components/primitives';
import { SurfaceHeader } from '../components/SurfaceHeader';

interface DormancyRule {
  propertyType: string;
  dormancyYears: number;
  note?: string;
}

const DEFAULT_RULES: DormancyRule[] = [
  { propertyType: 'Bank accounts / general', dormancyYears: 5 },
  { propertyType: 'Court bonds', dormancyYears: 1 },
  { propertyType: 'Demutualization insurance', dormancyYears: 2 },
  { propertyType: 'Business dissolution', dormancyYears: 2 },
  { propertyType: 'Payroll', dormancyYears: 3 },
  { propertyType: 'Gov/state agencies', dormancyYears: 3 },
  { propertyType: 'Money orders', dormancyYears: 7 },
  { propertyType: 'Travelers checks', dormancyYears: 15 },
];

export function Escheatment(): JSX.Element {
  const { config, state } = useConfig();

  const systemEscheatmentRules = useMemo<DormancyRule[]>(() => {
    const raw = config.systemSettings?.['MO_Escheatment_Rules'];
    if (Array.isArray(raw)) return raw as DormancyRule[];
    return DEFAULT_RULES;
  }, [config.systemSettings]);

  return (
    <div>
      <SurfaceHeader
        title="Escheatment / Dormancy Manager"
        scope="MO RSMo 447.539.9 unclaimed-property rules. Nov 1 reporting (June 30 cutoff); May 1 life insurance (Dec 31 cutoff). Due-diligence letters ≥ $50."
        surfaceNumber={7}
        role="FSR · Compliance oversight"
        actions={state === 'initializing' ? <Pill variant="warn">Config initializing</Pill> : undefined}
      />

      <section style={panelStyle}>
        <h3 style={{ marginTop: 0 }}>Dormancy rules (MO)</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--ink-50)', textAlign: 'left' }}>
              <th style={{ padding: 'var(--sp-3)' }}>Property type</th>
              <th style={{ padding: 'var(--sp-3)', textAlign: 'right' }}>Dormancy (years)</th>
            </tr>
          </thead>
          <tbody>
            {systemEscheatmentRules.map((r) => (
              <tr key={r.propertyType} style={{ borderTop: '1px solid var(--ink-200)' }}>
                <td style={{ padding: 'var(--sp-3)' }}>{r.propertyType}</td>
                <td style={{ padding: 'var(--sp-3)', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                  {r.dormancyYears}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section style={panelStyle}>
        <h3 style={{ marginTop: 0 }}>SDB escheatment timeline</h3>
        {config.sdbTimeline.length === 0 ? (
          <div style={{ color: 'var(--ink-600)', fontSize: 13 }}>
            No SDB timeline rows loaded from config.
          </div>
        ) : (
          <ul style={{ padding: 0, listStyle: 'none', display: 'grid', gap: 'var(--sp-3)' }}>
            {config.sdbTimeline.map((row) => (
              <li
                key={row.milestoneId}
                style={{
                  display: 'flex',
                  gap: 'var(--sp-4)',
                  padding: 'var(--sp-3) 0',
                  borderBottom: '1px solid var(--ink-100)',
                  fontSize: 13,
                }}
              >
                <span style={{ minWidth: 180, color: 'var(--pcb-blue-dark)', fontWeight: 600 }}>
                  {row.milestoneName}
                </span>
                <span style={{ color: 'var(--ink-700)', flex: 1 }}>
                  {row.action}
                  {row.notes ? ` — ${row.notes}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section style={panelStyle}>
        <h3 style={{ marginTop: 0 }}>Reporting reminders</h3>
        <ul style={{ fontSize: 13, color: 'var(--ink-700)', lineHeight: 1.7 }}>
          <li>November 1 — general escheatment report (cutoff June 30).</li>
          <li>May 1 — life insurance escheatment report (cutoff December 31).</li>
          <li>$50+ owed → due-diligence letter to last known address before reporting.</li>
          <li>$50 aggregate — under-$50 accounts bundled as "owner unknown."</li>
          <li>Format: NAUPA Standard Electronic XML for &gt; 50 owners; PDF &lt; 50.</li>
        </ul>
      </section>
    </div>
  );
}

const panelStyle: React.CSSProperties = {
  background: 'var(--surface-card)',
  border: '1px solid var(--surface-border)',
  borderRadius: 'var(--r-md)',
  padding: 'var(--sp-7)',
  marginBottom: 'var(--sp-6)',
};
